import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useBusinessAuth } from '../../business/context/BusinessAuthContext';
import RiderTrackingCard from '../components/RiderTrackingCard';
import { riderApi } from '../services/riderApi';

const TrackingContext = createContext(null);
export const useRequiredTracking = () => useContext(TrackingContext);
export const needsDeliveryTracking = run => Boolean(run && (
  ['READY', 'ACTIVE', 'INTERRUPTED'].includes(run.status)
  || (run.status === 'PLANNED' && run.pickupStartedAt)
));

// This owner stays mounted across business/rider navigation. The registry stores
// only workplace IDs; tracking credentials remain in the card's memory.
export default function RequiredTrackingProvider({ children }) {
  const auth = useBusinessAuth();
  const { pathname } = useLocation();
  const [entries, setEntries] = useState({});
  const [positions, setPositions] = useState({});
  const storageKey = auth.user?.id ? `apparify:required-tracking:${auth.user.id}` : null;
  const register = useCallback((businessId, run) => {
    if (!auth.isAuthenticated || !businessId) return;
    setEntries(previous => {
      const next = { ...previous };
      if (needsDeliveryTracking(run)) next[businessId] = { run, sessionKey: auth.token };
      else delete next[businessId];
      if (storageKey) localStorage.setItem(storageKey, JSON.stringify(Object.keys(next)));
      return next;
    });
  }, [auth.isAuthenticated, auth.token, storageKey]);

  useEffect(() => {
    setEntries({}); setPositions({});
    if (!auth.isAuthenticated || !storageKey) return undefined;
    const controller = new AbortController();
    let ids = [];
    try { ids = JSON.parse(localStorage.getItem(storageKey) || '[]'); } catch { /* Ignore corrupt recovery hints. */ }
    if (Array.isArray(ids)) for (const id of ids.filter(id => Number.isSafeInteger(Number(id)) && Number(id) > 0)) {
      riderApi.getCurrentRun(Number(id), { signal: controller.signal })
        .then(run => { if (!controller.signal.aborted) register(Number(id), run); })
        .catch(() => {});
    }
    return () => controller.abort();
  }, [auth.isAuthenticated, auth.token, storageKey, register]);

  const identities = Object.keys(entries).sort().join(',');
  useEffect(() => {
    // Rider work refresh owns this state while inside the rider portal.
    if (pathname.startsWith('/rider') || !identities) return undefined;
    const controller = new AbortController();
    let timer, failures = 0;
    const refresh = async () => {
      if (document.visibilityState === 'visible') {
        const results = await Promise.allSettled(identities.split(',').map(async id => {
          const run = await riderApi.getCurrentRun(Number(id), { signal: controller.signal });
          if (!controller.signal.aborted) register(Number(id), run);
        }));
        failures = results.some(result => result.status === 'rejected') ? failures + 1 : 0;
      }
      if (!controller.signal.aborted) timer = setTimeout(refresh, Math.min(120000, 15000 * 2 ** failures));
    };
    const resume = () => { if (document.visibilityState === 'visible') { clearTimeout(timer); refresh(); } };
    refresh(); document.addEventListener('visibilitychange', resume);
    return () => { controller.abort(); clearTimeout(timer); document.removeEventListener('visibilitychange', resume); };
  }, [identities, pathname, register]);

  const value = useMemo(() => ({ register, positions }), [register, positions]);
  return <TrackingContext.Provider value={value}>
    {children}
    <aside aria-label="Required delivery tracking" className="fixed bottom-24 right-3 z-40 w-[calc(100%-1.5rem)] max-w-sm space-y-2 text-stone-950 dark:text-white">
      {auth.isAuthenticated && Object.entries(entries).filter(([, entry]) => entry.sessionKey === auth.token).map(([id, entry]) => <TrackingEntry key={`${auth.token}:${id}:${entry.run.id}`} businessId={Number(id)} run={entry.run} setPositions={setPositions} />)}
    </aside>
  </TrackingContext.Provider>;
}

function TrackingEntry({ businessId, run, setPositions }) {
  const [state, setState] = useState('Starting');
  const onPosition = useCallback(position => setPositions(previous => ({ ...previous, [businessId]: position })), [businessId, setPositions]);
  return <details className="rounded-2xl border border-emerald-500 bg-white shadow-lg dark:bg-neutral-900">
    <summary className="min-h-11 cursor-pointer px-4 py-3 text-sm font-semibold">Location: {state} · {run.businessName || 'Delivery trip'}</summary>
    <div className="max-h-[60vh] overflow-y-auto p-2">
      <Link to="/rider/run" state={{ riderBusinessId: businessId }} className="inline-flex min-h-11 items-center px-3 underline">Return to this delivery</Link>
      <RiderTrackingCard businessId={businessId} run={run} required onPosition={onPosition} onTrackingState={setState} />
    </div>
  </details>;
}
